import {
  CompetitionLevelSchema,
  SportTypeSchema,
} from "@aura-frontend/omnibus-az-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Select } from "@win-reality/react-ui";
import { useForm } from "react-hook-form";
import { usaStates } from "typed-usa-states";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { competitionLevelsOptions } from "@/lib/competition-level-options";
import { sportsOptions } from "@/lib/sports-options";

const reviewFormSchema = z.object({
  accountName: z.string().min(1, "Required").max(100),
  sport: z.enum(SportTypeSchema.enum),
  competitionLevel: z.enum(CompetitionLevelSchema.enum),
  city: z.string().min(1, "Required"),
  state: z.string().min(1, "Required"),
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  accountOwner: z.string().min(1, "Required"),
});

export type ReviewFormData = z.infer<typeof reviewFormSchema>;

const stateItems = usaStates.map((option) => ({
  label: option.name,
  value: option.abbreviation,
}));

type FormWrapperProps = {
  isEditing: boolean;
  reviewFormData: ReviewFormData;
  onSubmit: (data: ReviewFormData) => void;
  onAccept: (data: ReviewFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
  isSuperAdmin: boolean;
};

export const ReviewAccountVerificationForm = ({
  isEditing,
  reviewFormData,
  onSubmit,
  onAccept,
  onCancel,
  isLoading,
  isSuperAdmin,
}: FormWrapperProps) => {
  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewFormSchema),
    defaultValues: reviewFormData,
  });

  const handleSubmit = (type: "accept" | "save") => (data: ReviewFormData) => {
    if (type === "accept") {
      onAccept(data);
    } else {
      onSubmit(data);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          handleSubmit(isEditing ? "save" : "accept"),
        )}
        className="grid grid-cols-2 gap-4 py-4"
      >
        <div>
          <FormField
            control={form.control}
            name="accountName"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Account Name:</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Account Name"
                    isDisabled={!isEditing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>City:</FormLabel>
                <FormControl>
                  <Input {...field} isDisabled={!isEditing} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={form.control}
            name="accountOwner"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Account Owner:</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Account Owner" isDisabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>State:</FormLabel>
                <FormControl>
                  <Select
                    items={stateItems}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select a state"
                    isDisabled={!isEditing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>First Name:</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="First Name" isDisabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={form.control}
            name="sport"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Sport:</FormLabel>
                <FormControl>
                  <Select
                    items={[...sportsOptions]}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select a sport"
                    isDisabled={!isEditing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Last Name:</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Last Name" isDisabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={form.control}
            name="competitionLevel"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Competition Level:</FormLabel>
                <FormControl>
                  <Select
                    items={[...competitionLevelsOptions]}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select a competition level"
                    isDisabled={!isEditing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-between gap-4 mt-4 col-span-2">
          <Button
            variant="ghost"
            type="button"
            onClick={onCancel}
            isDisabled={isLoading}
            className="underline -ml-5"
          >
            Cancel
          </Button>
          <TooltipProvider>
            <Tooltip delayDuration={0} open={!isSuperAdmin ? undefined : false}>
              <TooltipTrigger type="button" className="cursor-default" asChild>
                <div>
                  <Button
                    variant="secondary-flat-light"
                    type="submit"
                    data-testid="submit-button"
                    isLoading={isLoading}
                    isDisabled={isLoading || !isSuperAdmin}
                  >
                    {isEditing ? "Save and Accept" : "Accept"}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                Only super admins can verify users.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </form>
    </Form>
  );
};
